import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_EVENTS = [
  {
    id: '1',
    title: { it: 'Raduno Annuale', en: 'Annual Meetup', fr: 'Rassemblement Annuel', de: 'Jährliches Treffen', es: 'Encuentro Anual' },
    description: { it: 'Il nostro raduno annuale con climber da tutta Europa. Due giorni di arrampicata, workshop e socializzazione.', en: 'Our annual meetup with climbers from all over Europe. Two days of climbing, workshops and socializing.', fr: 'Notre rassemblement annuel avec des grimpeurs de toute l\'Europe.', de: 'Unser jährliches Treffen mit Kletterern aus ganz Europa.', es: 'Nuestro encuentro anual con escaladores de toda Europa.' },
    date: '2025-05-15',
    location: 'Caltavuturo',
    images: ['https://images.unsplash.com/photo-1522163182402-834f871fd851?w=600'],
    is_featured: true
  },
  {
    id: '2',
    title: { it: 'Corso Base Arrampicata', en: 'Basic Climbing Course', fr: 'Cours de Base d\'Escalade', de: 'Grundkurs Klettern', es: 'Curso Básico de Escalada' },
    description: { it: 'Corso introduttivo all\'arrampicata sportiva per principianti.', en: 'Introductory course to sport climbing for beginners.', fr: 'Cours d\'introduction à l\'escalade sportive pour débutants.', de: 'Einführungskurs ins Sportklettern für Anfänger.', es: 'Curso de introducción a la escalada deportiva para principiantes.' },
    date: '2025-06-01',
    location: 'Falesia di Gazzara',
    images: [],
    is_featured: false
  },
  {
    id: '3',
    title: { it: 'Giornata Pulizia Falesie', en: 'Crag Cleaning Day', fr: 'Journée de Nettoyage', de: 'Reinigungstag', es: 'Día de Limpieza' },
    description: { it: 'Giornata dedicata alla manutenzione e pulizia delle nostre falesie.', en: 'Day dedicated to maintenance and cleaning of our crags.', fr: 'Journée dédiée à l\'entretien et au nettoyage de nos falaises.', de: 'Tag für Wartung und Reinigung unserer Klettergärten.', es: 'Día dedicado al mantenimiento y limpieza de nuestros sectores.' },
    date: '2025-04-20',
    location: 'Tutte le falesie',
    images: [],
    is_featured: false
  }
];

export function EventsPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data || []);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Calendar className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('events')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Scopri i nostri eventi e partecipa alle attività dell\'associazione.'
                : 'Discover our events and participate in association activities.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events List */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {events.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border rounded-lg overflow-hidden hover:border-[#FF6D00]/50 transition-colors"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4">
                    {/* Image */}
                    <div className="md:col-span-1">
                      <div className="aspect-video md:aspect-square">
                        <img 
                          src={event.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=400'}
                          alt={getTranslatedContent(event.title)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <div className="flex items-center gap-2 text-[#FF6D00]">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm font-semibold">{formatDate(event.date)}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2 text-gray-500">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm">{event.location}</span>
                            </div>
                          )}
                          {event.is_featured && (
                            <span className="bg-[#FF6D00] text-white text-xs px-2 py-1 rounded uppercase">
                              Featured
                            </span>
                          )}
                        </div>

                        <h3 className="font-heading text-2xl uppercase mb-3">
                          {getTranslatedContent(event.title)}
                        </h3>
                        <p className="text-gray-600 line-clamp-2">
                          {getTranslatedContent(event.description)}
                        </p>
                      </div>

                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
